/**
 * Block renderer + admin editor primitives for the universal Pages CMS.
 *
 * Two storage shapes:
 *  - settings key `page_blocks:{pageId}` → array of after-hero content blocks
 *  - settings key `hero_slides:{pageId}` → array of carousel slides (Home only)
 *
 * Block schema:
 *   { id, type, heading, body, media_url, media_alt, align, media_side,
 *     background, cta_label, cta_href }
 *
 * Block types:
 *   text       — full-width heading + body
 *   image      — image with optional heading/caption
 *   video      — mp4/youtube with optional heading/caption
 *   media-row  — image + text side-by-side (alternating)
 *   quote      — large pull-quote with optional attribution
 *   cta        — heading + body + button
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { resolveMediaUrl } from "./PageContent";

const isUploadedVideo = (u) => !!u && (u.startsWith("/api/uploads/") || /\.(mp4|mov|webm|m4v)$/i.test(u));
const youtubeId = (u) => {
  if (!u) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(u)) return u;
  const m =
    u.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
    u.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
    u.match(/embed\/([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const BLOCK_TYPES = ["text", "image", "video", "media-row", "gallery", "quote", "cta", "divider"];

export const BLOCK_TYPE_LABELS = {
  text: "Text",
  image: "Image",
  video: "Video",
  "media-row": "Media + Text Row",
  gallery: "Image Gallery",
  quote: "Pull Quote",
  cta: "Call To Action",
  divider: "Divider",
};

export const newBlock = (type = "text") => ({
  id: cryptoId(),
  type,
  heading: "",
  body: "",
  media_url: "",
  media_alt: "",
  gallery: [],
  divider_style: "tape",
  align: "left",
  media_side: "left",
  background: "bone",
  cta_label: "",
  cta_href: "",
});

const cryptoId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }
};

// Available background classes
const BG_CLASS = {
  bone: "bg-bone text-asphalt",
  "card-light": "bg-card-light text-asphalt",
  asphalt: "bg-asphalt text-bone",
  terra: "bg-terra text-bone",
};

export const usePageBlocks = (pageId) => {
  const [blocks, setBlocks] = useState([]);
  useEffect(() => {
    let active = true;
    api
      .get(`/settings/page_blocks:${pageId}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        if (Array.isArray(v)) setBlocks(v);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pageId]);
  return blocks;
};

export const useHeroSlides = (pageId, defaults = []) => {
  const [slides, setSlides] = useState(defaults);
  useEffect(() => {
    let active = true;
    api
      .get(`/settings/hero_slides:${pageId}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        if (Array.isArray(v) && v.length) setSlides(v);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);
  return slides;
};

// ------------------------------------------------------------------
//   RENDERERS
// ------------------------------------------------------------------
export const PageBlocksRenderer = ({ blocks, testid = "page-blocks" }) => {
  if (!blocks || !blocks.length) return null;
  return (
    <div data-testid={testid}>
      {blocks.map((b, i) => (
        <BlockSection key={b.id || i} block={b} index={i} />
      ))}
    </div>
  );
};

const BlockSection = ({ block, index }) => {
  // divider has its own outer treatment (no padded section bg)
  if (block.type === "divider") {
    return <DividerBlock block={block} index={index} />;
  }
  const bg = BG_CLASS[block.background] || BG_CLASS.bone;
  return (
    <section className={`${bg} py-14 lg:py-20 border-b-2 border-asphalt/10`} data-testid={`block-${index}`}>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        {block.type === "text" && <TextBlock block={block} />}
        {block.type === "image" && <ImageBlock block={block} />}
        {block.type === "video" && <VideoBlock block={block} />}
        {block.type === "media-row" && <MediaRowBlock block={block} />}
        {block.type === "gallery" && <GalleryBlock block={block} />}
        {block.type === "quote" && <QuoteBlock block={block} />}
        {block.type === "cta" && <CTABlock block={block} />}
      </div>
    </section>
  );
};

const alignClass = (a) => (a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left");

const TextBlock = ({ block }) => (
  <div className={`max-w-3xl ${block.align === "center" ? "mx-auto" : ""} ${alignClass(block.align)}`}>
    {block.heading && (
      <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tight mb-5 whitespace-pre-line">{block.heading}</h2>
    )}
    {block.body && <p className="font-body text-lg leading-relaxed whitespace-pre-line">{block.body}</p>}
  </div>
);

const ImageBlock = ({ block }) => {
  const url = resolveMediaUrl(block.media_url);
  if (!url) return null;
  return (
    <figure className="max-w-5xl mx-auto">
      <div className="border-4 border-asphalt shadow-hard overflow-hidden">
        <MediaImage src={url} alt={block.media_alt || block.heading || ""} className="w-full h-auto ink-stamp" />
      </div>
      {(block.heading || block.body) && (
        <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.3em]">
          {block.heading && <span className="text-terra">✦ {block.heading}</span>}
          {block.body && <span className="block mt-1 text-ink/80">{block.body}</span>}
        </figcaption>
      )}
    </figure>
  );
};

// Shared image renderer with broken-image fallback.
// If the underlying URL 404s, swap to a tasteful "Media not available" placeholder so
// the page doesn't leak a broken-image icon to visitors.
const MediaImage = ({ src, alt, className }) => {
  const [broken, setBroken] = useState(false);
  /* eslint-disable */
  useEffect(() => { setBroken(false); }, [src]);
  /* eslint-enable */
  if (broken) {
    return (
      <div className={`${className || ""} bg-card-light flex items-center justify-center min-h-[120px]`}>
        <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-terra px-3 py-2">
          ✦ Media not available
        </div>
      </div>
    );
  }
  return <img src={src} alt={alt || ""} className={className} onError={() => setBroken(true)} loading="lazy" />;
};

const VideoBlock = ({ block }) => {
  const url = block.media_url;
  if (!url) return null;
  const full = resolveMediaUrl(url);
  const yt = !isUploadedVideo(url) ? youtubeId(url) : null;
  return (
    <div className="max-w-5xl mx-auto">
      {block.heading && (
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mb-4 whitespace-pre-line">{block.heading}</h2>
      )}
      <div className="border-4 border-asphalt shadow-hard aspect-video overflow-hidden bg-asphalt">
        {isUploadedVideo(url) ? (
          <video src={full} controls playsInline className="w-full h-full object-cover" />
        ) : yt ? (
          <iframe
            src={`https://www.youtube.com/embed/${yt}`}
            title={block.heading || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        ) : (
          <a href={url} target="_blank" rel="noreferrer" className="flex items-center justify-center w-full h-full text-bone font-mono text-xs uppercase tracking-[0.25em] underline">
            Open video →
          </a>
        )}
      </div>
      {block.body && <p className="font-body text-base leading-relaxed mt-4 whitespace-pre-line">{block.body}</p>}
    </div>
  );
};

const MediaRowBlock = ({ block }) => {
  const url = resolveMediaUrl(block.media_url);
  const mediaSide = block.media_side === "right" ? "lg:flex-row-reverse" : "lg:flex-row";
  return (
    <div className={`flex flex-col gap-8 items-center max-w-6xl mx-auto ${mediaSide}`}>
      {url && (
        <div className="lg:w-1/2 w-full border-4 border-asphalt shadow-hard overflow-hidden">
          {isUploadedVideo(block.media_url) ? (
            <video src={url} controls playsInline className="w-full h-full object-cover" />
          ) : (
            <MediaImage src={url} alt={block.media_alt || block.heading || ""} className="w-full h-auto ink-stamp" />
          )}
        </div>
      )}
      <div className="lg:w-1/2 w-full">
        {block.heading && (
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mb-3 whitespace-pre-line">{block.heading}</h2>
        )}
        {block.body && <p className="font-body text-base leading-relaxed whitespace-pre-line mb-4">{block.body}</p>}
        {block.cta_label && block.cta_href && (
          <Link
            to={block.cta_href}
            className="inline-flex items-center gap-2 border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-asphalt hover:text-bone"
          >
            {block.cta_label}
          </Link>
        )}
      </div>
    </div>
  );
};

const QuoteBlock = ({ block }) => (
  <blockquote className={`max-w-3xl mx-auto text-center`}>
    {block.body && (
      <p className="font-western text-3xl sm:text-5xl leading-tight">
        &ldquo;{block.body}&rdquo;
      </p>
    )}
    {block.heading && (
      <footer className="font-mono text-xs uppercase tracking-[0.3em] mt-6">— {block.heading}</footer>
    )}
  </blockquote>
);

const CTABlock = ({ block }) => (
  <div className="max-w-3xl mx-auto text-center">
    {block.heading && (
      <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mb-4 whitespace-pre-line">{block.heading}</h2>
    )}
    {block.body && <p className="font-body text-lg leading-relaxed mb-6 whitespace-pre-line">{block.body}</p>}
    {block.cta_label && block.cta_href && (
      <Link
        to={block.cta_href}
        className="inline-flex items-center gap-2 bg-terra text-bone border-2 border-asphalt px-7 py-4 font-display tracking-wider uppercase text-xl shadow-hard hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#1C1B1A] transition-all"
      >
        {block.cta_label}
      </Link>
    )}
  </div>
);

const GalleryBlock = ({ block }) => {
  const items = Array.isArray(block.gallery) ? block.gallery.filter(Boolean) : [];
  if (!items.length) return null;
  const cols = items.length >= 4 ? "md:grid-cols-4" : items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <div className="max-w-6xl mx-auto">
      {block.heading && (
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mb-6 whitespace-pre-line">{block.heading}</h2>
      )}
      <div className={`grid grid-cols-2 ${cols} gap-4`}>
        {items.map((url, i) => (
          <a
            key={`${url}-${i}`}
            href={resolveMediaUrl(url)}
            target="_blank"
            rel="noreferrer"
            className="border-4 border-asphalt shadow-hard bg-bone aspect-square overflow-hidden hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
          >
            <MediaImage
              src={resolveMediaUrl(url)}
              alt={block.media_alt || `${block.heading || "Gallery"} ${i + 1}`}
              className="w-full h-full object-cover ink-stamp"
            />
          </a>
        ))}
      </div>
      {block.body && <p className="font-body text-base leading-relaxed mt-5 whitespace-pre-line">{block.body}</p>}
    </div>
  );
};

const DividerBlock = ({ block, index }) => {
  const style = block.divider_style || "tape";
  // 'tape' uses the existing site decorative striped divider; 'rule' is a hard hairline.
  if (style === "rule") {
    return (
      <div className="bg-bone py-2" data-testid={`block-${index}`}>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="h-[3px] bg-asphalt my-6" />
        </div>
      </div>
    );
  }
  if (style === "stars") {
    return (
      <div className="bg-bone py-4" data-testid={`block-${index}`}>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 text-center font-mono text-xs uppercase tracking-[0.5em] text-terra">
          ✦ &nbsp; ✦ &nbsp; ✦
        </div>
      </div>
    );
  }
  return (
    <div className="bg-bone" data-testid={`block-${index}`}>
      <div className="tape-divider" />
    </div>
  );
};

// Helpers exported for the admin
export { BG_CLASS, BLOCK_TYPES, cryptoId };
